import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Spinner } from '@/components/ui';
import InfoTooltip from '../InfoTooltip';
import ParentWidget, { WidgetRenderProps, WidgetMetadata } from './ParentWidget';
import { usePermissions } from '../../hooks/usePermissions';
import {
  COLOR_PALETTE,
  getDarkerColor,
  groupByServiceArea,
  CHART_SIZE_STYLES,
  HOME_CHART_MIN_HEIGHT_PX,
  type ChartSize,
} from './shared/barGraphUtils';
import type { BarGraphSegmentConfig } from './ServiceAreaBarGraphWidget.types';

export type { BarGraphSegmentConfig } from './ServiceAreaBarGraphWidget.types';

export interface ServiceAreaBarGraphWidgetProps<T> {
  id: string;
  title: string;
  size?: ChartSize;
  data?: T[];
  config: BarGraphSegmentConfig<T>;
  redirect?: { path: string; queryParams?: Record<string, string | number | boolean> };
  metadata?: { loading?: boolean; error?: Error | null; lastUpdated?: Date | null };
  onRefresh?: () => void;
  customization?: { hidden?: boolean; order?: number; themeOverride?: Record<string, unknown> };
  showRedirectIndicator?: boolean;
  requiredPermission: string;
  /** Optional content above the stacked bar row (e.g. summary strip). */
  chartHeader?: React.ReactNode;
  /** Optional legend row below the bars (e.g. fixed status colors). */
  legend?: React.ReactNode;
  /** Optional content below the legend (e.g. secondary tables). */
  chartFooter?: React.ReactNode;
  /**
   * Render only the chart body (loading / empty / bars). Parent caller must wrap in `ParentWidget`
   * for permissions and chrome. Omit widget-level navigate on the outer chart wrapper.
   */
  omitParentChrome?: boolean;
}

function ServiceAreaBarGraphWidgetInner<T>({
  id,
  title,
  size = 'medium',
  data,
  config,
  redirect,
  metadata,
  onRefresh,
  customization,
  showRedirectIndicator = true,
  requiredPermission,
  chartHeader,
  legend,
  chartFooter,
  omitParentChrome = false,
}: ServiceAreaBarGraphWidgetProps<T>) {
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const [hoveredBar, setHoveredBar] = useState<number | null>(null);
  const [hoveredItem, setHoveredItem] = useState<T | null>(null);

  const handleForemanAreaClick = (e: React.MouseEvent, item: T) => {
    e.stopPropagation();
    const id = config.getForemanAreaId(item);
    navigate(config.getForemanAreaRedirect(id, item));
  };

  const handleForemanSubSegmentClick = (e: React.SyntheticEvent, item: T, subSegmentIndex: number) => {
    e.stopPropagation();
    const fn = config.getForemanAreaSubSegmentRedirect;
    if (!fn) return;
    const id = config.getForemanAreaId(item);
    navigate(fn(id, item, subSegmentIndex));
  };

  const handleServiceAreaClick = (
    e: React.MouseEvent,
    serviceAreaId: number,
    serviceAreaMeta?: { id: number; name: string }
  ) => {
    e.stopPropagation();
    navigate(config.getServiceAreaRedirect(serviceAreaId, serviceAreaMeta));
  };

  const chartStyles = CHART_SIZE_STYLES[size];

  const groupConfig = useMemo(
    () => ({
      getServiceArea: config.getServiceArea,
      getTotal: config.getTotal,
      getForemanAreaId: config.getForemanAreaId,
    }),
    [config]
  );

  const serviceAreaGroups = useMemo(() => {
    if (!data || data.length === 0) return [];
    return groupByServiceArea(data, groupConfig);
  }, [data, groupConfig]);

  const maxTotal = useMemo(() => {
    if (!serviceAreaGroups.length) return 1;
    return Math.max(...serviceAreaGroups.map((g) => g.total), 1);
  }, [serviceAreaGroups]);

  const parentlessMetadata: WidgetMetadata = useMemo(
    () => ({
      loading: metadata?.loading ?? false,
      error: metadata?.error ?? null,
      lastUpdated: metadata?.lastUpdated ?? null,
      isEmpty:
        !(metadata?.loading ?? false) &&
        !metadata?.error &&
        (!data?.length || serviceAreaGroups.length === 0),
    }),
    [metadata?.loading, metadata?.error, metadata?.lastUpdated, data?.length, serviceAreaGroups.length]
  );

  const renderChart = (props: WidgetRenderProps<T[]>) => {
    const { metadata: chartMetadata, helpers } = props;

    if (chartMetadata.loading) {
      return (
        <div
          className="tw-flex tw-justify-center tw-items-center"
          style={{ minHeight: `${HOME_CHART_MIN_HEIGHT_PX}px` }}
        >
          <Spinner animation="border" className="tw-text-primary">Loading...</Spinner>
        </div>
      );
    }

    if (chartMetadata.error) {
      return (
        <Alert variant="danger">
          Error loading data: {chartMetadata.error.message}
        </Alert>
      );
    }

    if (chartMetadata.isEmpty || !data?.length || serviceAreaGroups.length === 0) {
      const hasExtras = chartHeader != null || legend != null || chartFooter != null;
      return (
        <div className="service-area-chart-with-extras tw-w-full">
          {chartHeader ? <div className="tw-mb-2 tw-w-full">{chartHeader}</div> : null}
          <div className="tw-text-center tw-text-content-muted" style={{ padding: hasExtras ? '16px 0' : '40px 0' }}>
            <p className="tw-mb-0">{config.emptyMessage}</p>
          </div>
          {legend ? (
            <div className="tw-mt-2 tw-pt-2 tw-border-t tw-border-line tw-w-full" data-bargraph-control="true" onClick={(e) => e.stopPropagation()}>
              {legend}
            </div>
          ) : null}
          {chartFooter ? (
            <div className="tw-mt-3 tw-w-full tw-text-sm" data-bargraph-control="true" onClick={(e) => e.stopPropagation()}>
              {chartFooter}
            </div>
          ) : null}
        </div>
      );
    }

    return (
      <div className="service-area-chart-with-extras tw-w-full">
        {chartHeader ? <div className="tw-mb-2 tw-w-full">{chartHeader}</div> : null}
        <div
        className="stacked-bar-graph-container tw-flex tw-flex-col tw-items-center"
        onClick={() => redirect && helpers.navigate()}
        onKeyDown={(e) => {
          if (redirect && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            helpers.navigate();
          }
        }}
        role={redirect ? 'button' : undefined}
        tabIndex={redirect ? 0 : undefined}
        style={redirect ? { cursor: 'pointer' } : undefined}
      >
        <div
          className="tw-flex tw-items-end tw-justify-center tw-gap-3 tw-px-2 tw-w-full"
          style={{
            minHeight: `${HOME_CHART_MIN_HEIGHT_PX}px`,
            paddingBottom: '20px',
            paddingTop: '16px',
          }}
        >
          <div
            className="tw-flex tw-items-end tw-justify-center tw-gap-3"
            style={{ maxWidth: size === 'small' ? '320px' : '100%', margin: '0 auto' }}
          >
            {serviceAreaGroups.map((group) => (
              <div
                key={group.serviceArea.id}
                className="service-area-group tw-flex tw-flex-col tw-items-center"
                style={{
                  flex: '0 1 auto',
                  maxWidth: size === 'small' ? '80px' : '100px',
                  minWidth: size === 'small' ? '50px' : '70px',
                }}
              >
                <div className="tw-relative tw-flex tw-flex-col tw-items-center tw-w-full" style={{ gap: '8px' }}>
                  <span
                    className="tw-inline-flex tw-items-center tw-rounded-full tw-bg-ink tw-px-2 tw-py-0.5 tw-text-[0.68rem] tw-font-semibold tw-leading-none tw-text-white tw-tabular-nums"
                    title={`${group.total} total`}
                  >
                    {group.total.toLocaleString()}
                  </span>
                  <InfoTooltip
                    content={
                      hoveredItem ? (
                        <div>{config.getSegmentTooltip(hoveredItem)}</div>
                      ) : (
                        <span style={{ visibility: 'hidden' }}> </span>
                      )
                    }
                    id={`${id}-bar-tooltip-${group.serviceArea.id}`}
                    placement="top"
                    maxWidth="200px"
                  >
                    <div
                      style={{ display: 'block', width: '100%', minWidth: size === 'small' ? 50 : 70 }}
                      onMouseLeave={() => setHoveredItem(null)}
                    >
                      <div
                        className="tw-relative tw-flex tw-flex-col-reverse tw-w-full"
                        style={{
                          height: `${Math.max(24, (group.total / maxTotal) * 180)}px`,
                          backgroundColor: '#f1f5f9',
                          borderRadius: '5px 5px 1px 1px',
                          overflow: 'hidden',
                          boxShadow: '0 1px 2px rgba(15,23,42,0.12)',
                          cursor: redirect ? 'pointer' : 'default',
                          minHeight: 0,
                        }}
                        onMouseEnter={() => setHoveredBar(group.serviceArea.id)}
                        onMouseLeave={() => setHoveredBar(null)}
                      >
                        {group.items
                          .filter((item) => config.getTotal(item) > 0)
                          .map((item, index) => {
                            const total = config.getTotal(item);
                            const serviceAreaTotal = group.total;
                            const segmentShare = serviceAreaTotal > 0 ? total / serviceAreaTotal : 0;
                            const subSegments = config.getSubSegments?.(item);
                            const baseColor = COLOR_PALETTE[index % COLOR_PALETTE.length];
                            const isHovered = hoveredBar === group.serviceArea.id;

                            if (subSegments && subSegments.length > 0) {
                              const totalSub = subSegments.reduce((s, seg) => s + seg.value, 0);
                              const positiveSub = subSegments
                                .map((seg, segIdx) => ({ seg, segIdx }))
                                .filter(({ seg }) => seg.value > 0);
                              const perSliceDrill = Boolean(config.getForemanAreaSubSegmentRedirect);
                              return (
                                <div
                                  key={config.getForemanAreaId(item)}
                                  role={perSliceDrill ? undefined : 'button'}
                                  tabIndex={perSliceDrill ? undefined : 0}
                                  className="foreman-area-segment"
                                  style={{
                                    flex: `${total} 0 0`,
                                    minHeight: 0,
                                    width: '100%',
                                    display: 'flex',
                                    flexDirection: 'column-reverse',
                                    cursor: perSliceDrill ? 'default' : 'pointer',
                                  }}
                                  onClick={
                                    perSliceDrill
                                      ? undefined
                                      : (e) => {
                                          handleForemanAreaClick(e, item);
                                        }
                                  }
                                  onKeyDown={
                                    perSliceDrill
                                      ? undefined
                                      : (e) => {
                                          if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleForemanAreaClick(e as unknown as React.MouseEvent, item);
                                          }
                                        }
                                  }
                                  onMouseEnter={() => setHoveredItem(item)}
                                >
                                  {positiveSub.map(({ seg, segIdx }) => {
                                    const subShare = totalSub > 0 ? seg.value / totalSub : 0;
                                    return (
                                      <div
                                        key={segIdx}
                                        role={perSliceDrill ? 'button' : undefined}
                                        tabIndex={perSliceDrill ? 0 : undefined}
                                        style={{
                                          flex: `${seg.value} 0 0`,
                                          minHeight: 0,
                                          backgroundColor:
                                            seg.backgroundColor ??
                                            (seg.darken ? getDarkerColor(baseColor, 0.3) : baseColor),
                                          width: '100%',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          color: 'white',
                                          fontSize: size === 'small' ? '0.6rem' : '0.65rem',
                                          fontWeight: '600',
                                          textShadow: '0 1px 2px rgba(0,0,0,0.45)',
                                          cursor: perSliceDrill ? 'pointer' : undefined,
                                        }}
                                        onClick={
                                          perSliceDrill
                                            ? (e) => handleForemanSubSegmentClick(e, item, segIdx)
                                            : undefined
                                        }
                                        onKeyDown={
                                          perSliceDrill
                                            ? (e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                  e.preventDefault();
                                                  handleForemanSubSegmentClick(e, item, segIdx);
                                                }
                                              }
                                            : undefined
                                        }
                                      >
                                        {isHovered && seg.value > 0 && subShare > 0.12 && (
                                          <span>{seg.value}</span>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            }

                            return (
                              <div
                                key={config.getForemanAreaId(item)}
                                role="button"
                                tabIndex={0}
                                className="foreman-area-segment"
                                style={{
                                  flex: `${total} 0 0`,
                                  minHeight: 0,
                                  width: '100%',
                                  backgroundColor: baseColor,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'white',
                                  fontSize: size === 'small' ? '0.6rem' : '0.65rem',
                                  fontWeight: '600',
                                  textShadow: '0 1px 2px rgba(0,0,0,0.45)',
                                  cursor: 'pointer',
                                }}
                                onClick={(e) => handleForemanAreaClick(e, item)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleForemanAreaClick(e as unknown as React.MouseEvent, item);
                                  }
                                }}
                                onMouseEnter={() => setHoveredItem(item)}
                              >
                                {isHovered && total > 0 && segmentShare > 0.12 && <span>{total}</span>}
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </InfoTooltip>

                  <div
                    className="tw-flex tw-flex-col tw-items-center tw-w-full"
                    style={{ fontSize: chartStyles.labelFontSize, paddingTop: '4px' }}
                  >
                    <InfoTooltip
                      content={<div>{config.getGroupTooltip(group.items, group.serviceArea.name)}</div>}
                      id={`${id}-label-tooltip-${group.serviceArea.id}`}
                      placement="top"
                      maxWidth="200px"
                    >
                      <span
                        role="button"
                        tabIndex={0}
                        className="tw-font-medium tw-text-center tw-w-full"
                        style={{
                          fontSize: size === 'small' ? '0.7rem' : '0.75rem',
                          lineHeight: '1.2',
                          wordBreak: 'break-word',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                        }}
                        onClick={(e) =>
                          handleServiceAreaClick(e, group.serviceArea.id, {
                            id: group.serviceArea.id,
                            name: group.serviceArea.name,
                          })
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleServiceAreaClick(e as unknown as React.MouseEvent, group.serviceArea.id, {
                              id: group.serviceArea.id,
                              name: group.serviceArea.name,
                            });
                          }
                        }}
                      >
                        {group.serviceArea.name}
                      </span>
                    </InfoTooltip>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
        {legend ? (
          <div className="tw-mt-2 tw-pt-2 tw-border-t tw-border-line tw-w-full" data-bargraph-control="true" onClick={(e) => e.stopPropagation()}>
            {legend}
          </div>
        ) : null}
        {chartFooter ? (
          <div className="tw-mt-3 tw-w-full tw-text-sm" data-bargraph-control="true" onClick={(e) => e.stopPropagation()}>
            {chartFooter}
          </div>
        ) : null}
      </div>
    );
  };

  if (omitParentChrome) {
    if (!hasPermission(requiredPermission)) {
      return null;
    }
    const noopNavigate = () => {};
    return renderChart({
      data,
      metadata: parentlessMetadata,
      context: {
        id,
        title,
        size,
        redirect,
        hasRedirect: !!redirect,
      },
      helpers: {
        refresh: onRefresh ?? (() => {}),
        navigate: noopNavigate,
      },
    });
  }

  return (
    <ParentWidget<T[]>
      id={id}
      title={title}
      size={size}
      data={data}
      redirect={redirect}
      metadata={metadata}
      onRefresh={onRefresh}
      customization={customization}
      showRedirectIndicator={showRedirectIndicator}
      requiredPermission={requiredPermission}
    >
      {renderChart}
    </ParentWidget>
  );
}

export function ServiceAreaBarGraphWidget<T>(props: ServiceAreaBarGraphWidgetProps<T>) {
  return <ServiceAreaBarGraphWidgetInner {...props} />;
}

export default ServiceAreaBarGraphWidget;
